import type { LocalDate } from "../../../domain/time";

/** Pattern of a local date (`2026-10-12`). */
const localDatePattern = /^\d{4}-\d{2}-\d{2}$/u;

/** Router state with which the compact clock opens the day overview on the start page (F-75). */
export interface DayOverviewRequest {
  /** The working day whose day overview opens. */
  readonly dayOverview: LocalDate;
}

/**
 * Creates the router state that asks the start page to open the day overview of a day: after a
 * clock-out from the compact clock under „Abfrage beim Ausstempeln“ (spec 6.5.4, P-2).
 *
 * @param date - The working day.
 * @returns The router state.
 */
export function dayOverviewRequest(date: LocalDate): DayOverviewRequest {
  const request: DayOverviewRequest = { dayOverview: date };

  return request;
}

/**
 * Reads the day overview asked for in a router state; anything else is ignored.
 *
 * @param state - The router state of the location.
 * @returns The date, or `null`.
 */
export function readDayOverviewRequest(state: unknown): LocalDate | null {
  if (typeof state !== "object" || state === null || !("dayOverview" in state)) {
    return null;
  }

  const value: unknown = state.dayOverview;
  const date = typeof value === "string" && localDatePattern.test(value) ? value : null;

  return date;
}

/**
 * What the compact clock does when a clock-out asks for the day overview (F-78): `switch` leads to
 * „Heute“ at once, `ask` asks in the open panel first, `stay` keeps the page.
 */
export type DayOverviewHandover = "switch" | "ask" | "stay";

/** The situation in which a clock-out of the compact clock asks for the day overview. */
export interface DayOverviewHandoverSituation {
  /** Whether the panel of the compact clock is still open. */
  readonly panelOpen: boolean;
  /** Whether the current page holds input the person has not saved. */
  readonly unsavedInput: boolean;
}

/**
 * Decides how the compact clock leads to the day overview on „Heute“ after a clock-out under
 * „Abfrage beim Ausstempeln“ (spec 6.5.4, P-2): without unsaved input at once, as before (F-75);
 * with unsaved input only after a question in the open panel, so leaving the page never drops the
 * input unasked; with unsaved input and the panel already closed not at all – the question would
 * take the focus out of the form. The overview is a prompt, not a gate: the projects of the day can
 * be attributed later in „Meine Zeiten“ (T-125, spec 6.5.5).
 *
 * @param situation - Panel and page.
 * @returns What to do.
 */
export function dayOverviewHandover(situation: DayOverviewHandoverSituation): DayOverviewHandover {
  const handover: DayOverviewHandover = !situation.unsavedInput
    ? "switch"
    : situation.panelOpen
      ? "ask"
      : "stay";

  return handover;
}
